import React, { useState, useRef } from 'react';
import {
  Database,
  Plus,
  Trash2,
  Edit2,
  Check,
  X,
  Loader2,
  AlertCircle,
  Info,
  RefreshCw,
  Upload,
  Play,
  ChevronDown,
  ChevronUp,
  FileKey,
  Copy,
  ArrowRight,
  CheckCircle2,
  ExternalLink,
} from 'lucide-react';
import {
  OdbcConnectionsCoreProps,
  OdbcDsn,
  OdbcCredential,
  DsnTemplateField,
  WidgetTheme,
} from '../widget/types';
import { useWidgetTheme, mainAppTheme } from '../widget/useWidgetTheme';

const DSN_NAME_REGEX = /^[a-zA-Z0-9_-]{1,64}$/;

// ==================== STEP BANNER ====================

const StepBanner: React.FC<{ hasConnections: boolean }> = ({ hasConnections }) => (
  <div style={{
    display: 'flex',
    alignItems: 'stretch',
    gap: 0,
    backgroundColor: 'var(--v2-surface-alt)',
    border: '1px solid var(--v2-border)',
    borderRadius: 'var(--radius-lg)',
    marginBottom: '24px',
    overflow: 'hidden',
  }}>
    {[
      { num: 1, label: 'Install Driver', sub: 'ODBC Drivers tab', done: true },
      { num: 2, label: 'Configure Connection', sub: 'You are here', active: true },
      { num: 3, label: 'Create Database App', sub: 'Settings → Integrations', done: false },
    ].map((step, i) => (
      <React.Fragment key={step.num}>
        <div style={{
          flex: 1,
          padding: '14px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          backgroundColor: step.active ? 'var(--v2-primary-muted, rgba(99,102,241,0.07))' : 'transparent',
        }}>
          <div style={{
            width: '26px',
            height: '26px',
            borderRadius: '50%',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 'var(--text-xs)',
            fontWeight: 700,
            backgroundColor: step.active ? 'var(--v2-primary)' : step.done ? 'var(--v2-success)' : 'var(--v2-border)',
            color: (step.active || step.done) ? '#fff' : 'var(--v2-text-muted)',
          }}>
            {step.done && !step.active ? <Check size={13} strokeWidth={2.5} /> : step.num}
          </div>
          <div>
            <div style={{
              fontSize: 'var(--text-sm)',
              fontWeight: step.active ? 600 : 500,
              color: step.active ? 'var(--v2-text)' : step.done ? 'var(--v2-success)' : 'var(--v2-text-muted)',
              lineHeight: 'var(--leading-tight)',
            }}>
              {step.label}
            </div>
            <div style={{
              fontSize: 'var(--text-xs)',
              color: step.active ? 'var(--v2-text-secondary)' : 'var(--v2-text-tertiary)',
              marginTop: '1px',
              lineHeight: 'var(--leading-normal)',
            }}>
              {step.sub}
            </div>
          </div>
        </div>
        {i < 2 && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            padding: '0 2px',
            color: 'var(--v2-border)',
          }}>
            <ArrowRight size={14} />
          </div>
        )}
      </React.Fragment>
    ))}
  </div>
);

// ==================== DSN FORM ====================

interface DsnFormProps {
  mode: 'create' | 'edit';
  driverKey: string;
  dsnName: string;
  template: Record<string, DsnTemplateField>;
  initialConfig: Record<string, string>;
  requiresCredentialFile: boolean;
  credentialType: string | null;
  onSubmit: (data: any) => Promise<void>;
  onCancel: () => void;
  errorMessage?: string | null;
}

const DsnForm: React.FC<DsnFormProps> = ({
  mode,
  driverKey,
  dsnName: initialDsnName,
  template,
  initialConfig,
  requiresCredentialFile,
  credentialType,
  onSubmit,
  onCancel,
  errorMessage,
}) => {
  const [dsnName, setDsnName] = useState(initialDsnName);
  const [config, setConfig] = useState<Record<string, string>>(initialConfig);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);

  const templateFields = Object.entries(template || {});

  const handleFieldChange = (key: string, value: string) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  const validateName = (name: string) => {
    if (!name.trim()) return 'Connection name is required';
    if (!DSN_NAME_REGEX.test(name)) return 'Only letters, numbers, hyphens, underscores (max 64 chars)';
    return null;
  };

  const handleSubmit = async () => {
    if (mode === 'create') {
      const err = validateName(dsnName);
      if (err) { setNameError(err); return; }
    }
    setSaving(true);
    try {
      await onSubmit({
        dsn_name: dsnName,
        driver_key: driverKey,
        config,
        username: username || undefined,
        password: password || undefined,
      });
    } finally {
      setSaving(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '9px 12px',
    fontSize: 'var(--text-base)',
    border: '1px solid var(--v2-input-border)',
    borderRadius: 'var(--radius-md)',
    backgroundColor: 'var(--v2-surface)',
    color: 'var(--v2-text)',
    boxSizing: 'border-box',
    lineHeight: 'var(--leading-normal)',
    outline: 'none',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: 'var(--text-sm)',
    fontWeight: 500,
    color: 'var(--v2-text-secondary)',
    marginBottom: '5px',
    lineHeight: 'var(--leading-normal)',
  };

  return (
    <div style={{
      padding: '20px 24px',
      backgroundColor: 'var(--v2-surface)',
      borderRadius: 'var(--radius-lg)',
      border: '1px solid var(--v2-border)',
      marginBottom: '16px',
    }}>
      <h4 style={{
        margin: '0 0 16px 0',
        fontSize: 'var(--text-lg)',
        fontWeight: 600,
        color: 'var(--v2-text)',
        lineHeight: 'var(--leading-tight)',
      }}>
        {mode === 'create' ? 'New Connection' : `Edit: ${initialDsnName}`}
      </h4>

      {mode === 'create' && (
        <div style={{ marginBottom: '14px' }}>
          <label style={labelStyle}>Connection Name *</label>
          <input
            type="text"
            value={dsnName}
            onChange={(e) => { setDsnName(e.target.value); setNameError(null); }}
            placeholder="MySnowflake"
            maxLength={64}
            style={{ ...inputStyle, borderColor: nameError ? 'var(--v2-error)' : 'var(--v2-input-border)', fontFamily: 'var(--font-mono)' }}
          />
          {nameError && (
            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--v2-error)', marginTop: '3px', display: 'block' }}>{nameError}</span>
          )}
          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--v2-text-tertiary)', marginTop: '3px', display: 'block' }}>
            Letters, numbers, hyphens, underscores only
          </span>
        </div>
      )}

      {templateFields.map(([key, field]) => (
        <div key={key} style={{ marginBottom: '14px' }}>
          <label style={labelStyle}>{field.label}{field.required ? ' *' : ''}</label>
          {field.type === 'select' && field.options ? (
            <select
              value={config[key] || ''}
              onChange={(e) => handleFieldChange(key, e.target.value)}
              style={inputStyle}
            >
              <option value="">Select…</option>
              {field.options.map((opt, idx) => (
                <option key={opt} value={opt}>{field.option_labels?.[idx] ?? opt}</option>
              ))}
            </select>
          ) : (
            <input
              type={field.type === 'password' ? 'password' : 'text'}
              value={config[key] || ''}
              onChange={(e) => handleFieldChange(key, e.target.value)}
              placeholder={field.placeholder || ''}
              style={{ ...inputStyle, fontFamily: field.type === 'password' ? 'inherit' : 'var(--font-mono)' }}
            />
          )}
        </div>
      ))}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
        <div>
          <label style={labelStyle}>Username</label>
          <input type="text" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Optional" style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Password</label>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Optional" style={inputStyle} />
        </div>
      </div>

      {requiresCredentialFile && (
        <div style={{
          padding: '12px 14px',
          backgroundColor: 'var(--v2-info-bg)',
          border: '1px solid #93c5fd',
          borderRadius: 'var(--radius-md)',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '8px',
          marginBottom: '14px',
        }}>
          <FileKey size={14} style={{ color: 'var(--v2-primary)', marginTop: '2px', flexShrink: 0 }} />
          <span style={{ fontSize: 'var(--text-sm)', color: 'var(--v2-text-secondary)', lineHeight: 'var(--leading-relaxed)' }}>
            This driver requires a {credentialType?.replace(/_/g, ' ')} file. You can upload it after creating the connection.
          </span>
        </div>
      )}

{errorMessage && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: '10px',
          padding: '12px 14px', marginBottom: '14px',
          backgroundColor: 'var(--v2-error-bg)', border: '1px solid #fca5a5',
          borderRadius: 'var(--radius-md)',
        }}>
          <AlertCircle size={16} strokeWidth={1.5} color="var(--v2-error)" style={{ flexShrink: 0, marginTop: '1px' }} />
          <span style={{ fontSize: 'var(--text-sm)', color: 'var(--v2-error)', lineHeight: 'var(--leading-relaxed)' }}>
            {errorMessage}
          </span>
        </div>
      )}

      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
        <button
          onClick={onCancel}
          style={{
            padding: '8px 16px',
            fontSize: 'var(--text-sm)',
            fontWeight: 500,
            color: 'var(--v2-text-secondary)',
            backgroundColor: 'transparent',
            border: '1px solid var(--v2-border)',
            borderRadius: 'var(--radius-md)',
            cursor: 'pointer',
            minHeight: 'var(--target-md)',
            lineHeight: 'var(--leading-normal)',
          }}
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={saving}
          style={{
            padding: '8px 20px',
            fontSize: 'var(--text-sm)',
            fontWeight: 600,
            color: '#fff',
            backgroundColor: saving ? 'var(--v2-disabled-text)' : 'var(--v2-primary)',
            border: 'none',
            borderRadius: 'var(--radius-md)',
            cursor: saving ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            minHeight: 'var(--target-md)',
            lineHeight: 'var(--leading-normal)',
          }}
        >
          {saving && <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} />}
          {mode === 'create' ? 'Create Connection' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
};

// ==================== INLINE CREDENTIAL UPLOAD ====================

interface CredentialUploadProps {
  dsnName: string;
  dsns: OdbcDsn[];
  onUpload: (file: File, name: string, type: string, dsnName?: string) => Promise<void>;
  onSuccess?: (msg: string) => void;
  onError?: (msg: string) => void;
}

const CredentialUpload: React.FC<CredentialUploadProps> = ({ dsnName, dsns, onUpload, onSuccess, onError }) => {
  const [credName, setCredName] = useState('');
  const [credType, setCredType] = useState('');
  const [linkedDsn, setLinkedDsn] = useState(dsnName);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleUpload = async () => {
    setUploadError(null);
    if (!selectedFile) { setUploadError('Please select a credential file'); return; }
    if (!credName || !credType) { setUploadError('Name and type are required'); return; }
    setUploading(true);
    try {
      await onUpload(selectedFile, credName, credType, linkedDsn || undefined);
      setCredName('');
      setCredType('');
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      onSuccess?.('Credential uploaded');
    } catch (err: any) {
      setUploadError(err.message || 'Failed to upload credential');
    } finally {
      setUploading(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '7px 10px',
    fontSize: 'var(--text-sm)',
    border: '1px solid var(--v2-input-border)',
    borderRadius: 'var(--radius-md)',
    backgroundColor: 'var(--v2-surface)',
    color: 'var(--v2-text)',
    boxSizing: 'border-box',
    lineHeight: 'var(--leading-normal)',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: 'var(--text-xs)',
    fontWeight: 500,
    color: 'var(--v2-text-muted)',
    marginBottom: '4px',
    lineHeight: 'var(--leading-normal)',
  };

  const canUpload = !uploading && !!credName && !!credType && !!selectedFile;

  return (
    <div style={{
      marginTop: '14px',
      padding: '14px 16px',
      backgroundColor: 'var(--v2-surface-alt)',
      borderRadius: 'var(--radius-md)',
      border: '1px solid var(--v2-border)',
    }}>
      <div style={{
        fontSize: 'var(--text-xs)',
        fontWeight: 600,
        color: 'var(--v2-text-muted)',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        marginBottom: '10px',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
      }}>
        <Upload size={12} /> Upload Credential File
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
        <div>
          <label style={labelStyle}>Name *</label>
          <input type="text" value={credName} onChange={e => setCredName(e.target.value)} placeholder="e.g. BigQuery Prod Key" style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Type *</label>
          <select value={credType} onChange={e => setCredType(e.target.value)} style={inputStyle}>
            <option value="">Select type…</option>
            <option value="json_service_account">JSON Service Account</option>
            <option value="tls_ca_cert">TLS CA Certificate</option>
            <option value="tls_client_cert">TLS Client Certificate</option>
            <option value="tls_client_key">TLS Client Key</option>
            <option value="pat_token">PAT Token</option>
            <option value="oauth_json">OAuth JSON</option>
          </select>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '10px' }}>
        <div>
          <label style={labelStyle}>Link to Connection</label>
          <select value={linkedDsn} onChange={e => setLinkedDsn(e.target.value)} style={inputStyle}>
            <option value="">None (unlinked)</option>
            {dsns.map(d => <option key={d.dsn_name} value={d.dsn_name}>{d.dsn_name}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>File *</label>
          <input
            ref={fileInputRef}
            type="file"
            onChange={e => setSelectedFile(e.target.files?.[0] || null)}
            style={{ fontSize: 'var(--text-sm)', color: 'var(--v2-text)' }}
          />
        </div>
      </div>
{uploadError && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: '8px',
          padding: '10px 12px', marginBottom: '10px',
          backgroundColor: 'var(--v2-error-bg)', border: '1px solid #fca5a5',
          borderRadius: 'var(--radius-sm)',
        }}>
          <AlertCircle size={14} strokeWidth={1.5} color="var(--v2-error)" style={{ flexShrink: 0, marginTop: '1px' }} />
          <span style={{ fontSize: 'var(--text-sm)', color: 'var(--v2-error)', lineHeight: 'var(--leading-relaxed)' }}>
            {uploadError}
          </span>
        </div>
      )}

      <button
        onClick={handleUpload}
        disabled={!canUpload}
        style={{
          padding: '7px 16px',
          fontSize: 'var(--text-sm)',
          fontWeight: 600,
          color: '#fff',
          backgroundColor: canUpload ? 'var(--v2-primary)' : 'var(--v2-disabled-text)',
          border: 'none',
          borderRadius: 'var(--radius-md)',
          cursor: canUpload ? 'pointer' : 'not-allowed',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          minHeight: 'var(--target-md)',
          lineHeight: 'var(--leading-normal)',
        }}
      >
        {uploading && <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} />}
        Upload Credential
      </button>
    </div>
  );
};

// ==================== NEXT STEP CALLOUT ====================

const NextStepCallout: React.FC = () => (
  <div style={{
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    padding: '12px 16px',
    backgroundColor: 'var(--v2-success-bg, #f0fdf4)',
    border: '1px solid var(--v2-success)',
    borderRadius: 'var(--radius-md)',
    marginTop: '10px',
  }}>
    <CheckCircle2 size={15} style={{ color: 'var(--v2-success)', flexShrink: 0, marginTop: '1px' }} />
    <div>
      <div style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--v2-success-dark, #15803d)', lineHeight: 'var(--leading-tight)' }}>
        Connection test passed — ready for Step 3
      </div>
      <div style={{ fontSize: 'var(--text-sm)', color: 'var(--v2-text-secondary)', marginTop: '2px', lineHeight: 'var(--leading-relaxed)' }}>
        Go to <strong>Settings → Integrations</strong> in your workspace and create a <strong>Database App</strong> using type <strong>ODBC</strong> with this connection name.
      </div>
    </div>
  </div>
);

// ==================== MAIN COMPONENT ====================

const OdbcConnectionsCore: React.FC<OdbcConnectionsCoreProps> = ({
  dsns,
  credentials,
  installedDrivers,
  dsnTemplates,
  egressIps,
  loading,
  error,
  activeJobDsnId,
  activeJobStatus,
  mode: displayMode,
  theme: themeOverrides,
  onCreateDsn,
  onUpdateDsn,
  onDeleteDsn,
  onTestDsn,
  onUploadCredential,
  onDeleteCredential,
  onRefresh,
  onSuccess,
  onError,
}) => {
const [showCreateForm, setShowCreateForm] = useState(false);
  const [editDsnId, setEditDsnId] = useState<number | null>(null);
  const [expandedDsn, setExpandedDsn] = useState<number | null>(null);
  const [selectedDriver, setSelectedDriver] = useState<string>('');
  const [deleteModal, setDeleteModal] = useState<{ type: 'dsn' | 'credential'; id: number; name: string } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [createFormError, setCreateFormError] = useState<string | null>(null);
  const [editFormError, setEditFormError] = useState<string | null>(null);
  const [testingDsnId, setTestingDsnId] = useState<number | null>(null);
  const [refreshHover, setRefreshHover] = useState(false);
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);

const handleCreateDsn = async (data: any) => {
    setCreateFormError(null);
    try {
      await onCreateDsn(data);
      setShowCreateForm(false);
      setSelectedDriver('');
      onSuccess?.('Connection created');
    } catch (err: any) {
      setCreateFormError(err.message || 'Failed to create connection');
    }
  };

const handleUpdateDsn = async (data: any) => {
    if (!editDsnId) return;
    setEditFormError(null);
    try {
      await onUpdateDsn(editDsnId, data);
      setEditDsnId(null);
      onSuccess?.('Connection updated');
    } catch (err: any) {
      setEditFormError(err.message || 'Failed to update connection');
    }
  };

const handleDelete = async () => {
    if (!deleteModal) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      if (deleteModal.type === 'dsn') {
        await onDeleteDsn(deleteModal.id);
        onSuccess?.('Connection deleted');
      } else {
        await onDeleteCredential(deleteModal.id);
        onSuccess?.('Credential deleted');
      }
      setDeleteModal(null);
      setDeleteConfirmText('');
    } catch (err: any) {
      setDeleteError(err.message || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  const handleTest = async (dsnId: number) => {
    setTestingDsnId(dsnId);
    setExpandedDsn(dsnId); // always open so result is visible
    try {
      await onTestDsn(dsnId, undefined, undefined);
    } catch (err: any) {
      onError?.(err.message || 'Failed to test connection');
    } finally {
      setTestingDsnId(null);
    }
  };

  const isDsnBusy = (dsnId: number) =>
    (activeJobDsnId === dsnId && (activeJobStatus === 'pending' || activeJobStatus === 'processing'))
    || testingDsnId === dsnId;

  const getStatusStyle = (status: string | null): { color: string; label: string } => {
    if (status === 'success') return { color: 'var(--v2-success)', label: 'Connected' };
    if (status === 'failed') return { color: 'var(--v2-error)', label: 'Failed' };
    return { color: 'var(--v2-text-tertiary)', label: 'Untested' };
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '80px 0' }}>
        <div style={{
          width: '32px',
          height: '32px',
          border: '2px solid var(--v2-border)',
          borderTopColor: 'var(--v2-primary)',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
        }} />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        backgroundColor: 'var(--v2-error-bg)',
        border: '1px solid #fca5a5',
        borderRadius: 'var(--radius-lg)',
        padding: '16px 20px',
      }}>
        <AlertCircle size={18} strokeWidth={1.5} color="var(--v2-error)" style={{ flexShrink: 0, marginTop: '1px' }} />
        <div>
          <p style={{ fontSize: 'var(--text-base)', fontWeight: 500, color: 'var(--v2-error)', margin: 0 }}>{error}</p>
          <button
            onClick={() => onRefresh()}
            style={{
              marginTop: '8px',
              padding: '6px 12px',
              fontSize: 'var(--text-sm)',
              backgroundColor: 'var(--v2-surface)',
              border: '1px solid #fca5a5',
              borderRadius: 'var(--radius-sm)',
              cursor: 'pointer',
              color: 'var(--v2-error)',
            }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const tdStyle: React.CSSProperties = {
    padding: '14px 20px',
    borderBottom: '1px solid var(--v2-border-light)',
    verticalAlign: 'middle',
    fontSize: 'var(--text-base)',
    lineHeight: 'var(--leading-normal)',
  };

  return (
    <div style={{ fontFamily: 'var(--font-sans)' }}>

      {/* Step Banner */}
      <StepBanner hasConnections={dsns.length > 0} />

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
        <div>
          <h2 style={{
            margin: 0,
            fontSize: 'var(--text-xl)',
            fontWeight: 600,
            color: 'var(--v2-text)',
            lineHeight: 'var(--leading-tight)',
          }}>
            ODBC Connections
          </h2>
          <p style={{
            margin: '4px 0 0',
            fontSize: 'var(--text-base)',
            color: 'var(--v2-text-tertiary)',
            lineHeight: 'var(--leading-normal)',
          }}>
            {dsns.length} connection{dsns.length !== 1 ? 's' : ''} configured
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={() => onRefresh()}
            onMouseEnter={() => setRefreshHover(true)}
            onMouseLeave={() => setRefreshHover(false)}
            title="Refresh"
            style={{
              background: 'none',
              border: '1px solid var(--v2-border)',
              borderRadius: 'var(--radius-md)',
              padding: '8px 14px',
              cursor: 'pointer',
              color: refreshHover ? 'var(--v2-primary)' : 'var(--v2-text-tertiary)',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: 'var(--text-sm)',
              minHeight: 'var(--target-md)',
              transition: 'all 0.15s ease',
            }}
          >
            <RefreshCw size={14} /> Refresh
          </button>
          {installedDrivers.length > 0 && !showCreateForm && (
            <button
              onClick={() => setShowCreateForm(true)}
              style={{
                padding: '8px 16px',
                fontSize: 'var(--text-sm)',
                fontWeight: 600,
                color: '#fff',
                backgroundColor: 'var(--v2-primary)',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                minHeight: 'var(--target-md)',
                transition: 'background-color 0.15s ease',
              }}
            >
              <Plus size={14} /> New Connection
            </button>
          )}
        </div>
      </div>

      {/* No drivers installed warning */}
      {installedDrivers.length === 0 && (
        <div style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px',
          padding: '16px 20px',
          backgroundColor: 'var(--v2-info-bg)',
          border: '1px solid #93c5fd',
          borderRadius: 'var(--radius-lg)',
          marginBottom: '20px',
        }}>
          <Info size={18} strokeWidth={1.5} color="var(--v2-primary)" style={{ flexShrink: 0, marginTop: '1px' }} />
          <div>
            <div style={{ fontSize: 'var(--text-base)', fontWeight: 500, color: 'var(--v2-text)', lineHeight: 'var(--leading-tight)' }}>
              No ODBC drivers installed yet
            </div>
            <div style={{ fontSize: 'var(--text-sm)', color: 'var(--v2-text-secondary)', marginTop: '3px', lineHeight: 'var(--leading-relaxed)' }}>
              Go to the <strong>ODBC Drivers</strong> tab first and install a driver, then return here to configure your connection.
            </div>
          </div>
        </div>
      )}

      {/* Create Form */}
      {showCreateForm && (
        <>
          {!selectedDriver ? (
            <div style={{
              padding: '20px 24px',
              backgroundColor: 'var(--v2-surface)',
              borderRadius: 'var(--radius-lg)',
              border: '1px solid var(--v2-border)',
              marginBottom: '16px',
            }}>
              <div style={{ fontSize: 'var(--text-sm)', fontWeight: 500, color: 'var(--v2-text-secondary)', marginBottom: '12px' }}>
                Select a driver for this connection:
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '14px' }}>
                {installedDrivers.map(d => (
                  <button
                    key={d.driver_key}
                    onClick={() => setSelectedDriver(d.driver_key)}
                    style={{
                      padding: '8px 16px',
                      fontSize: 'var(--text-sm)',
                      fontWeight: 500,
                      color: 'var(--v2-text)',
                      backgroundColor: 'var(--v2-surface-alt)',
                      border: '1px solid var(--v2-border)',
                      borderRadius: 'var(--radius-md)',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    {d.driver_name}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setShowCreateForm(false)}
                style={{
                  padding: '6px 12px',
                  fontSize: 'var(--text-sm)',
                  color: 'var(--v2-text-tertiary)',
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          ) : (
<DsnForm
              mode="create"
              driverKey={selectedDriver}
              dsnName=""
              template={dsnTemplates[selectedDriver] || {}}
              initialConfig={{}}
              requiresCredentialFile={false}
              credentialType={null}
              onSubmit={handleCreateDsn}
              onCancel={() => { setShowCreateForm(false); setSelectedDriver(''); setCreateFormError(null); }}
              errorMessage={createFormError}
            />
          )}
        </>
      )}

      {/* Empty state */}
      {dsns.length === 0 && !showCreateForm ? (
        <div style={{
          padding: '48px 32px',
          textAlign: 'center',
          backgroundColor: 'var(--v2-surface)',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--v2-border)',
        }}>
          <Database size={32} strokeWidth={1.5} style={{ color: 'var(--v2-text-muted)', marginBottom: '12px' }} />
          <div style={{ fontSize: 'var(--text-base)', fontWeight: 500, color: 'var(--v2-text)', marginBottom: '6px' }}>
            No connections configured yet
          </div>
          <div style={{ fontSize: 'var(--text-sm)', color: 'var(--v2-text-tertiary)', marginBottom: '16px' }}>
            {installedDrivers.length > 0
              ? 'Click "New Connection" above to configure your first ODBC connection.'
              : 'Install a driver first, then create connections here.'}
          </div>
        </div>
      ) : dsns.length > 0 ? (
        /* Connection Table */
        <div style={{
          backgroundColor: 'var(--v2-surface)',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--v2-border)',
          overflow: 'hidden',
          marginBottom: '8px',
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Connection', 'Driver', 'Status', ''].map((h, i) => (
                  <th
                    key={i}
                    style={{
                      padding: '12px 20px',
                      fontSize: 'var(--text-xs)',
                      fontWeight: 500,
                      color: 'var(--v2-text-muted)',
                      textAlign: i === 3 ? 'right' : 'left',
                      borderBottom: '1px solid var(--v2-border)',
                      backgroundColor: 'var(--v2-surface-alt)',
                      lineHeight: 'var(--leading-normal)',
                      width: i === 3 ? '200px' : undefined,
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {dsns.map(dsn => {
                const busy = isDsnBusy(dsn.id);
                const expanded = expandedDsn === dsn.id;
                const editing = editDsnId === dsn.id;
                const dsnCredentials = credentials.filter(c => c.dsn_id === dsn.id);
                const statusStyle = getStatusStyle(dsn.last_test_status);
                const isHovered = hoveredRow === dsn.id;

                return (
                  <React.Fragment key={dsn.id}>
                    {editing ? (
                      <tr>
                        <td colSpan={4} style={{ padding: '16px 20px', borderBottom: '1px solid var(--v2-border)' }}>
<DsnForm
                            mode="edit"
                            driverKey={dsn.driver_key}
                            dsnName={dsn.dsn_name}
                            template={dsnTemplates[dsn.driver_key] || {}}
                            initialConfig={dsn.config}
                            requiresCredentialFile={false}
                            credentialType={null}
                            onSubmit={handleUpdateDsn}
                            onCancel={() => { setEditDsnId(null); setEditFormError(null); }}
                            errorMessage={editFormError}
                          />
                        </td>
                      </tr>
                    ) : (
                      <>
                        <tr
                          onMouseEnter={() => setHoveredRow(dsn.id)}
                          onMouseLeave={() => setHoveredRow(null)}
                          style={{
                            backgroundColor: isHovered ? 'var(--v2-surface-hover)' : 'var(--v2-surface)',
                            transition: 'background-color 0.1s ease',
                            cursor: 'pointer',
                          }}
                          onClick={() => setExpandedDsn(expanded ? null : dsn.id)}
                        >
                          {/* Name */}
                          <td style={tdStyle}>
                            <code style={{
                              fontSize: 'var(--text-base)',
                              fontWeight: 600,
                              fontFamily: 'var(--font-mono)',
                              color: 'var(--v2-text)',
                            }}>
                              {dsn.dsn_name}
                            </code>
                          </td>

                          {/* Driver */}
                          <td style={tdStyle}>
                            <span style={{
                              fontSize: 'var(--text-sm)',
                              color: 'var(--v2-text-secondary)',
                              backgroundColor: 'var(--v2-surface-alt)',
                              padding: '2px 8px',
                              borderRadius: '9999px',
                              border: '1px solid var(--v2-border)',
                            }}>
                              {dsn.driver_key}
                            </span>
                          </td>

                          {/* Status */}
                          <td style={tdStyle}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                              <span style={{
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                backgroundColor: statusStyle.color,
                                flexShrink: 0,
                              }} />
                              <span style={{ fontSize: 'var(--text-base)', color: 'var(--v2-text-secondary)' }}>
                                {statusStyle.label}
                              </span>
                            </div>
                          </td>

                          {/* Actions */}
                          <td style={{ ...tdStyle, textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'flex-end' }}>
                              {/* Test */}
                              <button
                                onClick={() => handleTest(dsn.id)}
                                disabled={busy}
                                title="Test connection"
                                style={{
                                  padding: '5px 12px',
                                  fontSize: 'var(--text-xs)',
                                  fontWeight: 600,
                                  color: busy ? 'var(--v2-text-muted)' : 'var(--v2-success)',
                                  backgroundColor: 'transparent',
                                  border: `1px solid ${busy ? 'var(--v2-border)' : 'var(--v2-success)'}`,
                                  borderRadius: 'var(--radius-sm)',
                                  cursor: busy ? 'not-allowed' : 'pointer',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  minHeight: 'var(--target-min)',
                                  lineHeight: 'var(--leading-normal)',
                                  transition: 'all 0.15s ease',
                                }}
                              >
                                {testingDsnId === dsn.id
                                  ? <Loader2 size={11} style={{ animation: 'spin 1s linear infinite' }} />
                                  : <Play size={11} />}
                                Test
                              </button>

                              {/* Edit */}
                              <button
                                onClick={() => setEditDsnId(dsn.id)}
                                title="Edit"
                                style={{
                                  padding: '5px 6px',
                                  background: 'none',
                                  border: '1px solid transparent',
                                  borderRadius: 'var(--radius-sm)',
                                  cursor: 'pointer',
                                  color: 'var(--v2-text-tertiary)',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  minHeight: 'var(--target-min)',
                                  transition: 'all 0.15s ease',
                                }}
                              >
                                <Edit2 size={14} />
                              </button>

                              {/* Delete */}
                              <button
                                onClick={() => setDeleteModal({ type: 'dsn', id: dsn.id, name: dsn.dsn_name })}
                                title="Delete"
                                style={{
                                  padding: '5px 6px',
                                  background: 'none',
                                  border: '1px solid transparent',
                                  borderRadius: 'var(--radius-sm)',
                                  cursor: 'pointer',
                                  color: 'var(--v2-text-tertiary)',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  minHeight: 'var(--target-min)',
                                  transition: 'all 0.15s ease',
                                }}
                              >
                                <Trash2 size={14} />
                              </button>

                              {/* Expand toggle */}
                              <button
                                onClick={() => setExpandedDsn(expanded ? null : dsn.id)}
                                style={{
                                  padding: '5px 6px',
                                  background: 'none',
                                  border: '1px solid transparent',
                                  borderRadius: 'var(--radius-sm)',
                                  cursor: 'pointer',
                                  color: 'var(--v2-text-tertiary)',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  minHeight: 'var(--target-min)',
                                }}
                              >
                                {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                              </button>
                            </div>
                          </td>
                        </tr>

                        {/* Expanded row */}
                        {expanded && (
                          <tr>
                            <td colSpan={4} style={{
                              padding: '16px 20px 20px',
                              borderBottom: '1px solid var(--v2-border)',
                              backgroundColor: 'var(--v2-surface-alt)',
                            }}>

                              {/* Config fields grid */}
                              {Object.keys(dsn.config).length > 0 && (
                                <div style={{
                                  display: 'grid',
                                  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                                  gap: '8px',
                                  marginBottom: '12px',
                                }}>
                                  {Object.entries(dsn.config).map(([k, v]) => {
                                    const isSensitive = /password|secret|key|token|pwd/i.test(k);
                                    return (
                                      <div key={k} style={{
                                        padding: '8px 10px',
                                        backgroundColor: 'var(--v2-surface)',
                                        borderRadius: 'var(--radius-sm)',
                                        border: '1px solid var(--v2-border)',
                                      }}>
                                        <div style={{
                                          fontSize: 'var(--text-xs)',
                                          fontWeight: 600,
                                          color: 'var(--v2-text-tertiary)',
                                          textTransform: 'uppercase',
                                          letterSpacing: '0.05em',
                                          marginBottom: '3px',
                                        }}>
                                          {k}
                                        </div>
                                        <div style={{
                                          fontSize: 'var(--text-sm)',
                                          fontFamily: 'var(--font-mono)',
                                          color: 'var(--v2-text)',
                                          wordBreak: 'break-all',
                                        }}>
                                          {isSensitive ? '••••••••' : v as string}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}

                              {/* Testing in-progress indicator */}
                              {testingDsnId === dsn.id && (
                                <div style={{
                                  padding: '10px 14px',
                                  backgroundColor: 'var(--v2-surface)',
                                  border: '1px solid var(--v2-border)',
                                  borderRadius: 'var(--radius-sm)',
                                  marginBottom: '10px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '10px',
                                }}>
                                  <Loader2 size={14} style={{ color: 'var(--v2-primary)', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
                                  <span style={{ fontSize: 'var(--text-sm)', color: 'var(--v2-text-secondary)' }}>
                                    Testing connection…
                                  </span>
                                  <div style={{
                                    marginLeft: 'auto',
                                    display: 'flex',
                                    gap: '4px',
                                    alignItems: 'center',
                                  }}>
                                    {[0, 150, 300].map(delay => (
                                      <div key={delay} style={{
                                        width: '6px',
                                        height: '6px',
                                        borderRadius: '50%',
                                        backgroundColor: 'var(--v2-primary)',
                                        animation: `pulse 1.2s ease-in-out ${delay}ms infinite`,
                                      }} />
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Test result */}
                              {dsn.last_test_message && testingDsnId !== dsn.id && (
                                <div style={{
                                  padding: '8px 12px',
                                  backgroundColor: dsn.last_test_status === 'success' ? 'var(--v2-success-bg, #f0fdf4)' : 'var(--v2-error-bg)',
                                  border: `1px solid ${dsn.last_test_status === 'success' ? 'var(--v2-success)' : '#fca5a5'}`,
                                  borderRadius: 'var(--radius-sm)',
                                  marginBottom: '10px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                }}>
                                  <span style={{
                                    fontSize: 'var(--text-sm)',
                                    color: dsn.last_test_status === 'success' ? 'var(--v2-success)' : 'var(--v2-error)',
                                  }}>
                                    {dsn.last_test_message}
                                  </span>
                                  {dsn.last_test_at && (
                                    <span style={{ fontSize: 'var(--text-xs)', color: 'var(--v2-text-tertiary)', marginLeft: 'auto' }}>
                                      {new Date(dsn.last_test_at).toLocaleString()}
                                    </span>
                                  )}
                                </div>
                              )}

                              {/* Next Step callout for passing tests */}

                              {dsn.last_test_status === 'success' && <NextStepCallout />}

                              {/* Existing credentials */}
                              {dsnCredentials.length > 0 && (
                                <div style={{ marginTop: '14px' }}>
                                  <div style={{
                                    fontSize: 'var(--text-xs)',
                                    fontWeight: 600,
                                    color: 'var(--v2-text-muted)',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.06em',
                                    marginBottom: '6px',
                                  }}>
                                    Credentials
                                  </div>
                                  {dsnCredentials.map(c => (
                                    <div key={c.id} style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      padding: '7px 10px',
                                      backgroundColor: 'var(--v2-surface)',
                                      border: '1px solid var(--v2-border)',
                                      borderRadius: 'var(--radius-sm)',
                                      marginBottom: '4px',
                                    }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <FileKey size={13} style={{ color: 'var(--v2-text-muted)' }} />
                                        <span style={{ fontSize: 'var(--text-sm)', color: 'var(--v2-text)' }}>{c.credential_name}</span>
                                        <span style={{
                                          fontSize: 'var(--text-xs)',
                                          color: 'var(--v2-text-tertiary)',
                                          backgroundColor: 'var(--v2-surface-alt)',
                                          padding: '1px 6px',
                                          borderRadius: '9999px',
                                          border: '1px solid var(--v2-border)',
                                        }}>
                                          {c.credential_type}
                                        </span>
                                      </div>
                                      <button
                                        onClick={() => setDeleteModal({ type: 'credential', id: c.id, name: c.credential_name })}
                                        style={{
                                          background: 'none',
                                          border: 'none',
                                          cursor: 'pointer',
                                          padding: '3px',
                                          color: 'var(--v2-text-tertiary)',
                                          display: 'flex',
                                          alignItems: 'center',
                                        }}
                                      >
                                        <Trash2 size={13} />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Inline credential upload */}
                              <CredentialUpload
                                dsnName={dsn.dsn_name}
                                dsns={dsns}
                                onUpload={onUploadCredential}
                                onSuccess={onSuccess}
                                onError={onError}
                              />

                              {/* Created by */}
                              <div style={{
                                marginTop: '12px',
                                fontSize: 'var(--text-xs)',
                                color: 'var(--v2-text-tertiary)',
                              }}>
                                Created by {dsn.created_by_email} on {new Date(dsn.created_at).toLocaleDateString()}
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}

      {/* Delete Modal — type-to-confirm */}
      {deleteModal && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          animation: 'fadeIn 0.15s ease',
        }}>
          <div style={{
            backgroundColor: 'var(--v2-surface)',
            borderRadius: 'var(--radius-lg)',
            border: '1px solid #fca5a5',
            maxWidth: '460px',
            width: '90%',
            boxShadow: 'var(--shadow-lg)',
            animation: 'slideDown 0.15s ease',
            overflow: 'hidden',
          }}>
            {/* Red header bar */}
            <div style={{
              padding: '16px 24px',
              backgroundColor: 'var(--v2-error-bg)',
              borderBottom: '1px solid #fca5a5',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
            }}>
              <AlertCircle size={18} strokeWidth={2} style={{ color: 'var(--v2-error)', flexShrink: 0 }} />
              <h4 style={{ margin: 0, fontSize: 'var(--text-lg)', fontWeight: 600, color: 'var(--v2-error)', lineHeight: 'var(--leading-tight)' }}>
                {deleteModal.type === 'dsn' ? 'Delete Connection' : 'Delete Credential'}
              </h4>
            </div>

            <div style={{ padding: '20px 24px 24px' }}>
              {/* Consequences */}
              {deleteModal.type === 'dsn' ? (
                <div style={{
                  padding: '12px 14px',
                  backgroundColor: 'var(--v2-error-bg)',
                  border: '1px solid #fca5a5',
                  borderRadius: 'var(--radius-md)',
                  marginBottom: '20px',
                }}>
                  <div style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--v2-error)', marginBottom: '6px' }}>
                    This will permanently:
                  </div>
                  <ul style={{ margin: 0, paddingLeft: '18px', fontSize: 'var(--text-sm)', color: 'var(--v2-error)', lineHeight: 'var(--leading-relaxed)' }}>
                    <li>Remove the DSN <strong>{deleteModal.name}</strong> from your server</li>
                    <li>Delete all associated credential files</li>
                    <li>Break any Database Apps in your workspace using this connection</li>
                  </ul>
                </div>
              ) : (
                <div style={{
                  padding: '12px 14px',
                  backgroundColor: 'var(--v2-error-bg)',
                  border: '1px solid #fca5a5',
                  borderRadius: 'var(--radius-md)',
                  marginBottom: '20px',
                }}>
                  <div style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--v2-error)', marginBottom: '6px' }}>
                    This will permanently:
                  </div>
                  <ul style={{ margin: 0, paddingLeft: '18px', fontSize: 'var(--text-sm)', color: 'var(--v2-error)', lineHeight: 'var(--leading-relaxed)' }}>
                    <li>Delete the credential file <strong>{deleteModal.name}</strong></li>
                    <li>Break connections that rely on this credential for authentication</li>
                  </ul>
                </div>
              )}

              {/* Type to confirm */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 500,
                  color: 'var(--v2-text-secondary)',
                  marginBottom: '6px',
                  lineHeight: 'var(--leading-normal)',
                }}>
                  Type <strong style={{ fontFamily: 'var(--font-mono)', color: 'var(--v2-text)' }}>delete</strong> to confirm
                </label>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={e => setDeleteConfirmText(e.target.value)}
                  placeholder="delete"
                  autoFocus
                  onKeyDown={e => { if (e.key === 'Enter' && deleteConfirmText === 'delete') handleDelete(); }}
                  style={{
                    width: '100%',
                    padding: '9px 12px',
                    fontSize: 'var(--text-base)',
                    fontFamily: 'var(--font-mono)',
                    border: `1px solid ${deleteConfirmText === 'delete' ? 'var(--v2-error)' : 'var(--v2-input-border)'}`,
                    borderRadius: 'var(--radius-md)',
                    backgroundColor: 'var(--v2-surface)',
                    color: 'var(--v2-text)',
                    boxSizing: 'border-box',
                    lineHeight: 'var(--leading-normal)',
                    outline: 'none',
                  }}
                />
              </div>

{deleteError && (
                <div style={{
                  display: 'flex', alignItems: 'flex-start', gap: '10px',
                  padding: '12px 14px', marginBottom: '16px',
                  backgroundColor: 'var(--v2-error-bg)', border: '1px solid #fca5a5',
                  borderRadius: 'var(--radius-md)',
                }}>
                  <AlertCircle size={16} strokeWidth={1.5} color="var(--v2-error)" style={{ flexShrink: 0, marginTop: '1px' }} />
                  <span style={{ fontSize: 'var(--text-sm)', color: 'var(--v2-error)', lineHeight: 'var(--leading-relaxed)' }}>
                    {deleteError}
                  </span>
                </div>
              )}

              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => { setDeleteModal(null); setDeleteConfirmText(''); setDeleteError(null); }}
                  disabled={deleting}
                  style={{
                    padding: '8px 16px',
                    fontSize: 'var(--text-sm)',
                    fontWeight: 500,
                    color: 'var(--v2-text-secondary)',
                    backgroundColor: 'transparent',
                    border: '1px solid var(--v2-border)',
                    borderRadius: 'var(--radius-md)',
                    cursor: 'pointer',
                    minHeight: 'var(--target-md)',
                    lineHeight: 'var(--leading-normal)',
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleting || deleteConfirmText !== 'delete'}
                  style={{
                    padding: '8px 20px',
                    fontSize: 'var(--text-sm)',
                    fontWeight: 600,
                    color: '#fff',
                    backgroundColor: (deleting || deleteConfirmText !== 'delete') ? 'var(--v2-disabled-text)' : 'var(--v2-error)',
                    border: 'none',
                    borderRadius: 'var(--radius-md)',
                    cursor: (deleting || deleteConfirmText !== 'delete') ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    minHeight: 'var(--target-md)',
                    lineHeight: 'var(--leading-normal)',
                    transition: 'background-color 0.15s ease',
                  }}
                >
                  {deleting && <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} />}
                  Permanently Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.3; transform: scale(0.75); }
        }
      `}</style>
    </div>
  );
};

export default OdbcConnectionsCore;
